import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Lock, History, Settings, X, Receipt, AlertTriangle,
  ChevronRight, CheckCircle2,
} from "lucide-react";
import { getToken } from "../../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHdr = () => ({ Authorization: `Bearer ${getToken()}` });

// One-way state machine. Mirrors backend _VALID_TRANSITIONS. Rendering only.
const NEXT_STATUS = {
  pending: "approved",      // via finalise()
  approved: "available",
  available: "processing",
  processing: "paid",
  paid: null,
};

const STATUS_META = {
  pending:    { chip: "bg-amber-100 text-amber-900 border-amber-300", label: "pending" },
  approved:   { chip: "bg-indigo-100 text-indigo-900 border-indigo-300", label: "approved" },
  available:  { chip: "bg-sky-100 text-sky-900 border-sky-300", label: "available" },
  processing: { chip: "bg-violet-100 text-violet-900 border-violet-300", label: "processing" },
  paid:       { chip: "bg-emerald-100 text-emerald-900 border-emerald-300", label: "paid" },
};

// UK convention: negative amounts render as "-£1.00", not "£-1.00".
const fmtGbp = (v) => {
  if (v == null || v === "") return "—";
  const s = String(v);
  return s.startsWith("-") ? `-£${s.slice(1)}` : `£${s}`;
};
// Orphan-safe partner label: "(deleted partner) · abc12345" when a ledger row
// references a partner_id that no longer exists in the partners collection.
const partnerLabelFor = (partnerById, partnerId) => {
  const p = partnerById?.[partnerId];
  if (p?.label) return p.label;
  const short = (partnerId || "").replace(/-/g, "").slice(0, 8);
  return `(deleted partner) · ${short || "unknown"}`;
};
// Config-snapshot cell: show "—" for null / missing values on legacy rows.
const snapCell = (v) => (v == null || v === "" ? "—" : v);
const defaultPeriod = () => {
  const d = new Date();
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

export default function CommissionsPanel() {
  const [period, setPeriod] = useState(defaultPeriod());
  const [partnerFilter, setPartnerFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [partners, setPartners] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null); // full row id
  const [recomputing, setRecomputing] = useState(false);

  const partnerById = useMemo(
    () => Object.fromEntries(partners.map((p) => [p.id, p])),
    [partners],
  );

  const loadPartners = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/partners`, { headers: authHdr() });
      setPartners(r.data?.partners || []);
    } catch (e) {
      toast.error("Could not load partners.");
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/commissions/config`, { headers: authHdr() });
      setConfig(r.data?.config || null);
    } catch {
      toast.error("Could not load commission config.");
    }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (period) params.period = period;
      if (partnerFilter) params.partner_id = partnerFilter;
      if (statusFilter) params.status = statusFilter;
      const r = await axios.get(`${API}/admin/commissions`, { headers: authHdr(), params });
      setRows(r.data?.commissions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load commissions.");
    } finally { setLoading(false); }
  }, [period, partnerFilter, statusFilter]);

  useEffect(() => { loadPartners(); loadConfig(); }, [loadPartners, loadConfig]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const onRecompute = async () => {
    if (!period) { toast.error("Pick a period first."); return; }
    const scope = partnerFilter
      ? partnerById[partnerFilter]?.label || "selected partner"
      : "ALL active partners";
    if (!window.confirm(`Recompute ${period} for ${scope}?\n\nFinalised rows are untouched; pending rows will refresh with current config and transactions.`))
      return;
    setRecomputing(true);
    try {
      const body = { period, ...(partnerFilter ? { partner_id: partnerFilter } : {}) };
      const r = await axios.post(`${API}/admin/commissions/recompute`, body, { headers: authHdr() });
      toast.success(`Recomputed ${r.data?.commissions?.length ?? 0} row(s).`);
      await loadRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recompute failed.");
    } finally { setRecomputing(false); }
  };

  return (
    <div data-testid="admin-commissions-panel">
      {/* Legal + context banner */}
      <div className="mb-6 border border-indigo-200 bg-indigo-50/60 rounded-md px-4 py-3 text-sm text-indigo-900" data-testid="commissions-banner">
        <div className="flex items-start gap-2">
          <Receipt className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Commission Ledger — Admin review</p>
            <p className="text-xs text-indigo-800/80 mt-0.5">
              Figures below are computed by the backend from eligible <span className="font-mono">payment_transactions</span>.
              Finalised rows are immutable. No payout is ever triggered from this screen —
              status transitions are an auditable record only. MyAE is a trading name of OpenDegree Ltd.
            </p>
          </div>
        </div>
      </div>

      {/* Config banner */}
      <ConfigBanner config={config} onEdit={() => setConfigOpen(true)} />

      {/* Filters + actions */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Period</label>
          <input
            type="month" value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="h-9 px-2 border border-border rounded text-sm font-mono"
            data-testid="commissions-filter-period"
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Partner</label>
          <select
            value={partnerFilter}
            onChange={(e) => setPartnerFilter(e.target.value)}
            className="h-9 px-2 border border-border rounded text-sm min-w-[200px]"
            data-testid="commissions-filter-partner"
          >
            <option value="">All partners</option>
            {partners.map((p) => (
              <option key={p.id} value={p.id}>{p.label} · {p.commission_rate_pct}%</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 px-2 border border-border rounded text-sm"
            data-testid="commissions-filter-status"
          >
            <option value="">Any status</option>
            {Object.keys(STATUS_META).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end gap-2 ml-auto">
          <button
            onClick={loadRows}
            disabled={loading}
            className="h-9 px-3 text-sm border border-border rounded hover:bg-muted/50 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="commissions-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
          <button
            onClick={onRecompute}
            disabled={recomputing}
            className="h-9 px-3 text-sm bg-oxford text-white rounded hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="commissions-recompute"
          >
            {recomputing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Recompute period
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="border border-border rounded overflow-x-auto">
        <table className="w-full text-[13px]" data-testid="commissions-table">
          <thead className="bg-muted/50 text-left">
            <tr>
              <Th>Partner</Th>
              <Th>Period</Th>
              <Th className="text-right">Txns</Th>
              <Th className="text-right">Users</Th>
              <Th className="text-right">Gross</Th>
              <Th className="text-right">VAT</Th>
              <Th className="text-right">Net</Th>
              <Th className="text-right">Refunds</Th>
              <Th className="text-right">Stripe fees</Th>
              <Th className="text-right">AI costs</Th>
              <Th className="text-right">Profit</Th>
              <Th className="text-right">Rate</Th>
              <Th className="text-right">Commission</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={15} className="px-3 py-10 text-center text-muted-foreground">
                No commission rows for the current filter. Pick a period and click <span className="font-medium">Recompute period</span> to build the ledger.
              </td></tr>
            )}
            {loading && (
              <tr><td colSpan={15} className="px-3 py-6 text-center text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin inline mr-1.5" /> Loading…
              </td></tr>
            )}
            {rows.map((r) => (
              <Row
                key={r.id}
                row={r}
                partnerLabel={partnerLabelFor(partnerById, r.partner_id)}
                onOpen={() => setDetailRow(r.id)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {configOpen && (
        <ConfigModal
          config={config}
          onClose={() => setConfigOpen(false)}
          onSaved={async () => { await loadConfig(); setConfigOpen(false); }}
        />
      )}
      {detailRow && (
        <DetailDrawer
          commissionId={detailRow}
          partnerById={partnerById}
          onClose={() => setDetailRow(null)}
          onChanged={async () => { await loadRows(); }}
        />
      )}
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-2.5 py-2 font-medium text-xs uppercase tracking-wide text-muted-foreground ${className}`}>{children}</th>
);
const Td = ({ children, className = "", testId }) => (
  <td className={`px-2.5 py-2 ${className}`} data-testid={testId}>{children}</td>
);

function Row({ row, partnerLabel, onOpen }) {
  const meta = STATUS_META[row.status] || STATUS_META.pending;
  const locked = !!row.finalised_at;
  return (
    <tr className="border-t border-border hover:bg-muted/30" data-testid={`commission-row-${row.id}`}>
      <Td className="font-medium">{partnerLabel}</Td>
      <Td className="font-mono">{row.period}</Td>
      <Td className="text-right tabular-nums">{row.payment_count}</Td>
      <Td className="text-right tabular-nums">{row.user_count}</Td>
      <Td className="text-right tabular-nums">{fmtGbp(row.gross_revenue_gbp)}</Td>
      <Td className="text-right tabular-nums">{fmtGbp(row.vat_amount_gbp ?? "0.00")}</Td>
      <Td className="text-right tabular-nums">{fmtGbp(row.net_revenue_gbp ?? row.gross_revenue_gbp)}</Td>
      <Td className="text-right tabular-nums">{fmtGbp(row.refunds_gbp)}</Td>
      <Td className="text-right tabular-nums">{fmtGbp(row.stripe_fees_gbp)}</Td>
      <Td className="text-right tabular-nums">{fmtGbp(row.ai_costs_gbp)}</Td>
      <Td className="text-right tabular-nums">{fmtGbp(row.contribution_profit_gbp)}</Td>
      <Td className="text-right tabular-nums">{row.commission_rate_pct}%</Td>
      <Td className="text-right tabular-nums font-semibold">{fmtGbp(row.commission_gbp)}</Td>
      <Td>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs ${meta.chip}`} data-testid={`commission-status-${row.id}`}>
          {locked && <Lock className="w-3 h-3" />}
          {meta.label}
        </span>
      </Td>
      <Td>
        <button
          onClick={onOpen}
          className="text-xs px-2 py-1 border border-border rounded hover:bg-muted/50 inline-flex items-center gap-1"
          data-testid={`commission-open-${row.id}`}
        >
          View <ChevronRight className="w-3 h-3" />
        </button>
      </Td>
    </tr>
  );
}

function ConfigBanner({ config, onEdit }) {
  if (!config) return null;
  return (
    <div className="mb-5 border border-border bg-muted/30 rounded-md p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="commissions-config-banner">
      <div className="flex flex-wrap gap-4 text-xs">
        <ConfigChip label="Stripe fee" value={`${config.stripe_fee_pct}% + £${config.stripe_fee_fixed_gbp}`} />
        <ConfigChip label="AI / paid user" value={`£${config.ai_cost_per_user_gbp}`} />
        <ConfigChip
          label="VAT"
          value={Number(config.vat_rate_pct) > 0
            ? `${config.vat_rate_pct}% (inclusive)`
            : "0.00% (not applicable)"}
        />
      </div>
      <button
        onClick={onEdit}
        className="h-8 px-3 text-xs border border-border rounded hover:bg-muted/50 inline-flex items-center gap-1.5"
        data-testid="commissions-config-edit"
      >
        <Settings className="w-3.5 h-3.5" /> Edit config
      </button>
    </div>
  );
}

const ConfigChip = ({ label, value }) => (
  <div><span className="text-muted-foreground">{label}:</span> <span className="font-mono">{value}</span></div>
);

// ---------------- Config modal ----------------
function ConfigModal({ config, onClose, onSaved }) {
  const [stripeFeePct, setStripeFeePct] = useState(config?.stripe_fee_pct || "2.90");
  const [stripeFixed, setStripeFixed] = useState(config?.stripe_fee_fixed_gbp || "0.20");
  const [aiPerUser, setAiPerUser] = useState(config?.ai_cost_per_user_gbp || "1.50");
  const [vatPct, setVatPct] = useState(config?.vat_rate_pct || "0.00");
  const [notes, setNotes] = useState(config?.notes || "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!reason.trim()) { toast.error("Reason is required for config changes."); return; }
    if (saving) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/commissions/config`, {
        stripe_fee_pct: stripeFeePct,
        stripe_fee_fixed_gbp: stripeFixed,
        ai_cost_per_user_gbp: aiPerUser,
        vat_rate_pct: vatPct,
        notes,
        reason,
      }, { headers: authHdr() });
      toast.success("Commission config updated. Pending rows will reflect the new values on next recompute.");
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <Modal onClose={onClose} title="Edit commission config" testId="config-modal">
      <div className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Stripe fee (%)">
            <input value={stripeFeePct} onChange={(e) => setStripeFeePct(e.target.value)}
              className="w-full border border-border rounded px-2 h-9 font-mono"
              data-testid="config-stripe-pct" />
          </Field>
          <Field label="Stripe fee fixed (£)">
            <input value={stripeFixed} onChange={(e) => setStripeFixed(e.target.value)}
              className="w-full border border-border rounded px-2 h-9 font-mono"
              data-testid="config-stripe-fixed" />
          </Field>
          <Field label="AI cost per paid user (£)">
            <input value={aiPerUser} onChange={(e) => setAiPerUser(e.target.value)}
              className="w-full border border-border rounded px-2 h-9 font-mono"
              data-testid="config-ai-per-user" />
          </Field>
          <Field label="VAT rate (%)">
            <input value={vatPct} onChange={(e) => setVatPct(e.target.value)}
              className="w-full border border-border rounded px-2 h-9 font-mono"
              data-testid="config-vat-pct" />
            <p className="text-[11px] text-muted-foreground mt-1">
              Prices are treated as VAT-inclusive at this rate. Default <span className="font-mono">0.00</span> because
              the current Stripe checkout does not configure automatic_tax.
            </p>
          </Field>
        </div>
        <Field label="Notes">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
            className="w-full border border-border rounded px-2 py-1.5 text-sm" />
        </Field>
        <Field label="Reason (required)">
          <input value={reason} onChange={(e) => setReason(e.target.value)}
            className="w-full border border-border rounded px-2 h-9"
            data-testid="config-reason" placeholder="Why is this changing?" />
        </Field>
        <div className="border border-amber-200 bg-amber-50 rounded px-3 py-2 text-xs text-amber-900 inline-flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Finalised rows ignore this change — their <span className="font-mono">config_snapshot</span> is frozen. Pending rows refresh on next recompute.</span>
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="h-9 px-3 text-sm border border-border rounded hover:bg-muted/50">Cancel</button>
          <button onClick={save} disabled={saving}
            className="h-9 px-3 text-sm bg-oxford text-white rounded hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="config-save">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save config
          </button>
        </div>
      </div>
    </Modal>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs text-muted-foreground mb-1">{label}</span>
    {children}
  </label>
);

// ---------------- Detail drawer ----------------
function DetailDrawer({ commissionId, partnerById, onClose, onChanged }) {
  const [row, setRow] = useState(null);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState("");       // which action is in flight
  const [refundAmt, setRefundAmt] = useState("");
  const [refundReason, setRefundReason] = useState("");

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/commissions/${commissionId}`, { headers: authHdr() });
      setRow(r.data?.commission || null);
      const a = await axios.get(`${API}/admin/commissions/${commissionId}/audit`, { headers: authHdr() });
      setAudit(a.data?.audit || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load detail.");
    }
  }, [commissionId]);

  useEffect(() => { refresh(); }, [refresh]);

  const guardedAction = async (key, fn, confirmMsg) => {
    if (busy) return; // prevent duplicate submits
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(key);
    try {
      await fn();
      await refresh();
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
    } finally { setBusy(""); }
  };

  const finalise = () => guardedAction(
    "finalise",
    async () => {
      const reason = window.prompt("Reason for finalising (locks the row)?", "Reviewed + inputs locked");
      if (reason === null) return;
      await axios.post(`${API}/admin/commissions/${commissionId}/finalise`,
        { reason }, { headers: authHdr() });
      toast.success("Finalised — row is now immutable.");
    },
    `Finalise this row?\n\nAfter finalisation:\n • Config and transaction changes will NOT affect this row.\n • Status moves pending → approved.\n • This CANNOT be undone from the UI.`,
  );

  const transitionTo = (target) => guardedAction(
    `transition-${target}`,
    async () => {
      const reason = window.prompt(`Reason for status → ${target}?`, "");
      if (reason === null) return;
      await axios.post(`${API}/admin/commissions/${commissionId}/transition`,
        { status: target, reason }, { headers: authHdr() });
      toast.success(`Status → ${target}`);
    },
    `Move this commission to "${target}"?\n\nThis is an audit record only — no payout is triggered.`,
  );

  const applyRefund = () => guardedAction(
    "refund",
    async () => {
      if (!refundAmt || !refundReason.trim()) {
        toast.error("Amount and reason are both required.");
        throw new Error("missing");
      }
      await axios.post(`${API}/admin/commissions/${commissionId}/refund-adjustment`,
        { amount_gbp: refundAmt, reason: refundReason }, { headers: authHdr() });
      toast.success("Refund adjustment applied.");
      setRefundAmt(""); setRefundReason("");
    },
    `Apply a refund adjustment of £${refundAmt || "0.00"} to this pending row?\n\nIt will be deducted from gross before contribution-profit is computed.`,
  );

  if (!row) {
    return (
      <Modal onClose={onClose} title="Commission detail" wide testId="detail-drawer">
        <div className="py-10 text-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
        </div>
      </Modal>
    );
  }

  const meta = STATUS_META[row.status] || STATUS_META.pending;
  const partnerLabel = partnerLabelFor(partnerById, row.partner_id);
  const locked = !!row.finalised_at;
  const nxt = NEXT_STATUS[row.status];
  const canFinalise = row.status === "pending" && !locked;
  const canTransition = !!nxt && row.status !== "pending" && locked; // approved→… only after finalise
  const canRefund = row.status === "pending" && !locked;
  const snap = row.config_snapshot || {};
  const rs = row.refund_source || {};

  return (
    <Modal onClose={onClose} title={`Commission · ${partnerLabel} · ${row.period}`} wide testId="detail-drawer">
      <div className="space-y-5 text-sm">
        {/* Status + lock banner */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs ${meta.chip}`} data-testid="detail-status">
            {locked && <Lock className="w-3 h-3" />} {meta.label}
          </span>
          {locked && (
            <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
              <Lock className="w-3 h-3" /> Finalised at {row.finalised_at} — read-only.
            </span>
          )}
          <span className="text-xs text-muted-foreground ml-auto font-mono">calc: {row.calc_version}</span>
        </div>

        {/* Figures grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border border border-border rounded overflow-hidden">
          <Metric label="Gross revenue" value={fmtGbp(row.gross_revenue_gbp)} />
          <Metric label="VAT amount" value={fmtGbp(row.vat_amount_gbp ?? "0.00")} />
          <Metric label="Net revenue" value={fmtGbp(row.net_revenue_gbp ?? row.gross_revenue_gbp)} />
          <Metric label="Refunds" value={fmtGbp(row.refunds_gbp)} />
          <Metric label="Stripe fees" value={fmtGbp(row.stripe_fees_gbp)} />
          <Metric label="AI costs" value={fmtGbp(row.ai_costs_gbp)} />
          <Metric label="Contribution profit" value={fmtGbp(row.contribution_profit_gbp)} />
          <Metric label={`Commission (${row.commission_rate_pct}%)`} value={fmtGbp(row.commission_gbp)} emphasis />
        </div>

        {/* Config snapshot */}
        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
            Config snapshot (frozen on this row)
          </h4>
          <div className="border border-border rounded p-2.5 text-xs grid grid-cols-2 sm:grid-cols-5 gap-2 font-mono bg-muted/20" data-testid="detail-config-snapshot">
            <span>Fee: {snap.stripe_fee_pct == null ? "—" : `${snap.stripe_fee_pct}%`}</span>
            <span>Fixed: {snap.stripe_fee_fixed_gbp == null ? "—" : `£${snap.stripe_fee_fixed_gbp}`}</span>
            <span>AI/user: {snap.ai_cost_per_user_gbp == null ? "—" : `£${snap.ai_cost_per_user_gbp}`}</span>
            <span>VAT: {snap.vat_rate_pct == null ? "—" : `${snap.vat_rate_pct}%`}</span>
            <span>Treatment: {snapCell(snap.vat_treatment)}</span>
          </div>
        </section>

        {/* Refund source */}
        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
            Refund source
          </h4>
          <div className="border border-border rounded p-2.5 text-xs space-y-1" data-testid="detail-refund-source">
            <div className="font-mono">
              Scanned from txns: £{rs.scanned_from_txns_gbp ?? "0.00"} ·
              Admin adjustment: £{rs.admin_adjustment_gbp ?? "0.00"}
            </div>
            {rs.refund_txn_ids?.length > 0 && (
              <div className="font-mono text-muted-foreground">txn ids: {rs.refund_txn_ids.join(", ")}</div>
            )}
            {rs.note && <p className="text-muted-foreground">{rs.note}</p>}
          </div>

          {canRefund && (
            <div className="mt-2 border border-dashed border-border rounded p-2.5 bg-muted/20">
              <p className="text-xs font-medium mb-2">Apply admin refund adjustment</p>
              <div className="flex gap-2 items-end flex-wrap">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Amount (£)</label>
                  <input value={refundAmt} onChange={(e) => setRefundAmt(e.target.value)}
                    placeholder="0.00" className="h-9 px-2 border border-border rounded text-sm font-mono w-28"
                    data-testid="detail-refund-amount" />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs text-muted-foreground mb-1">Reason</label>
                  <input value={refundReason} onChange={(e) => setRefundReason(e.target.value)}
                    placeholder="e.g. Stripe refund 2026-02-14 cs_abc"
                    className="h-9 px-2 border border-border rounded text-sm w-full"
                    data-testid="detail-refund-reason" />
                </div>
                <button onClick={applyRefund} disabled={busy === "refund"}
                  className="h-9 px-3 text-sm bg-oxford text-white rounded hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
                  data-testid="detail-refund-apply">
                  {busy === "refund" && <Loader2 className="w-4 h-4 animate-spin" />} Apply
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Actions */}
        <section className="flex flex-wrap gap-2 pt-2 border-t border-border">
          {canFinalise && (
            <button onClick={finalise} disabled={!!busy}
              className="h-9 px-3 text-sm bg-indigo-600 text-white rounded hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="detail-finalise">
              {busy === "finalise" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Finalise & approve
            </button>
          )}
          {canTransition && (
            <button onClick={() => transitionTo(nxt)} disabled={!!busy}
              className="h-9 px-3 text-sm bg-oxford text-white rounded hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid={`detail-transition-${nxt}`}>
              {busy === `transition-${nxt}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Mark as {nxt}
            </button>
          )}
          {row.status === "paid" && (
            <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Terminal state — this record is complete.
            </span>
          )}
          {!canFinalise && !canTransition && row.status !== "paid" && (
            <span className="text-xs text-muted-foreground">No further actions available at this status.</span>
          )}
        </section>

        {/* Audit history */}
        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5 inline-flex items-center gap-1">
            <History className="w-3.5 h-3.5" /> Audit history
          </h4>
          {audit.length === 0 ? (
            <p className="text-xs text-muted-foreground">No audit entries yet.</p>
          ) : (
            <div className="border border-border rounded max-h-60 overflow-y-auto" data-testid="detail-audit-list">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-left"><tr>
                  <th className="px-2 py-1.5">When</th>
                  <th className="px-2 py-1.5">Type</th>
                  <th className="px-2 py-1.5">Actor</th>
                  <th className="px-2 py-1.5">Reason</th>
                </tr></thead>
                <tbody>
                  {audit.map((a, i) => (
                    <tr key={a.id || i} className="border-t border-border">
                      <td className="px-2 py-1.5 font-mono text-[11px]">{a.created_at || a.timestamp || "—"}</td>
                      <td className="px-2 py-1.5">{a.type}</td>
                      <td className="px-2 py-1.5">{a.actor?.email || a.actor_email || a.actor?.user_id || "—"}</td>
                      <td className="px-2 py-1.5">{a.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </Modal>
  );
}

const Metric = ({ label, value, emphasis = false }) => (
  <div className={`px-3 py-2 bg-background ${emphasis ? "sm:col-span-1" : ""}`}>
    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`font-mono tabular-nums mt-0.5 ${emphasis ? "text-base font-semibold text-oxford" : "text-sm"}`}>{value}</p>
  </div>
);

function Modal({ children, title, onClose, wide = false, testId }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/40 p-4 overflow-y-auto" data-testid={testId}>
      <div className={`bg-white rounded-lg shadow-xl w-full ${wide ? "max-w-3xl" : "max-w-xl"} my-6`}>
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="font-medium">{title}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
